// Report figures and invoices: spec/algorithm.md §9–§10 applied to groups of time. Mirrors TallyCore/Reports.swift.
// Each project merges on its own; a group's billed time is the sum of its projects' unions. Groups that split a project
// (source, provider) can overlap each other, so their rows may add up to more than the total: totals are always computed
// on their own, never as the sum of rows.
import { billProjects, clipIntervals, mergeIntervals, roundForExport, sumIntervals, type BillingProject, type ProjectTotals } from './billing.ts';
import { zoneOffsetMs, zonedMidnight } from './time.ts';
import type { Interval } from './types.ts';

const DAY_MS = 86_400_000;
const MAX_BUCKETS = 400;

export type ReportSource = 'human' | 'agent';

/** One piece of counted time: an entry of yours or a piece of agent working time (corrections applied). */
export interface ReportItem {
  project: string | null;
  source: ReportSource;
  /** Provider id for agent time (`claude-code`, `codex` …); absent or `null` for your own time. */
  provider?: string | null;
  start: number;
  /** `null` while running (ends at `now`). */
  end: number | null;
  /** The entry's flag; agent time is billable whenever its project is. */
  billable: boolean;
  /** Whose time this is: in a team each member's time merges on its own (spec §9b). */
  member?: string | null;
}

export interface ReportTotals {
  /** Merged per project, then summed: the headline ("Abgerechnet"). */
  billedMs: number;
  /** Plain sum of all sources. */
  rawMs: number;
  humanMs: number;
  agentMs: number;
  /** Merged billable time (billable projects and entries). */
  billableMs: number;
  amountCents: number;
}

/** Time counted once although several sources worked at the same time. */
export function overlapMs(totals: ReportTotals): number {
  return Math.max(0, totals.rawMs - totals.billedMs);
}

/** Per-project billing of `items` (the same numbers the timeline and exports use). Human items are entries, agent items spans. */
export function reportPerProject(items: readonly ReportItem[], projects: readonly BillingProject[], range: Interval, now: number): Map<string, ProjectTotals> {
  return billProjects({
    projects,
    entries: items.filter((i) => i.source === 'human'),
    agentSpans: items.filter((i) => i.source === 'agent'),
    now,
    range,
  });
}

/** Totals of `items` within `range`. Time without a project is not billed and is left out. */
export function reportTotals(items: readonly ReportItem[], projects: readonly BillingProject[], range: Interval, now: number): ReportTotals {
  const totals: ReportTotals = { billedMs: 0, rawMs: 0, humanMs: 0, agentMs: 0, billableMs: 0, amountCents: 0 };
  for (const t of reportPerProject(items, projects, range, now).values()) {
    totals.billedMs += t.mergedMs;
    totals.rawMs += t.rawMs;
    totals.humanMs += t.humanMs;
    totals.agentMs += t.agentMs;
    totals.billableMs += t.billableMs;
    totals.amountCents += t.amountCents;
  }
  return totals;
}

/** Totals per group key, each group computed on its own. Items without a key (`null`, `undefined`) and groups without time are left out. */
export function reportGrouped(
  items: readonly ReportItem[],
  projects: readonly BillingProject[],
  range: Interval,
  now: number,
  key: (item: ReportItem) => string | null | undefined,
): Map<string, ReportTotals> {
  const groups = new Map<string, ReportItem[]>();
  for (const item of items) {
    const k = key(item);
    if (k === null || k === undefined) continue;
    const list = groups.get(k) ?? [];
    list.push(item);
    groups.set(k, list);
  }
  const result = new Map<string, ReportTotals>();
  for (const [k, list] of groups) {
    const totals = reportTotals(list, projects, range, now);
    if (totals.rawMs > 0) result.set(k, totals);
  }
  return result;
}

/** The union of time without a project within `range` (shown as a hint, never billed). */
export function unassignedMs(items: readonly ReportItem[], range: Interval, now: number): number {
  const intervals = items.filter((i) => i.project === null).map((i) => ({ start: i.start, end: i.end ?? now }));
  return sumIntervals(mergeIntervals(clipIntervals(intervals, range.start, range.end)));
}

export type BucketSize = 'day' | 'week' | 'month';

/** A date whose UTC fields show the wall clock of `timeZone` at instant `t`. */
function wallClock(t: number, timeZone: string): Date {
  return new Date(t + zoneOffsetMs(t, timeZone));
}

/** Monday 0 … Sunday 6. */
function isoWeekday(wall: Date): number {
  return (wall.getUTCDay() + 6) % 7;
}

/** Local days, ISO weeks (from Monday) or calendar months covering `range` in `timeZone`, each clipped to it; at most 400. */
export function reportBuckets(range: Interval, size: BucketSize, timeZone: string): Interval[] {
  const first = wallClock(range.start, timeZone);
  const year = first.getUTCFullYear();
  let month = first.getUTCMonth() + 1;
  let day = size === 'month' ? 1 : first.getUTCDate() - (size === 'week' ? isoWeekday(first) : 0);
  // Steps through local dates (zonedMidnight rolls them over), so a 23 or 25 hour day is one bucket and the cursor always advances.
  let cursor = zonedMidnight(year, month, day, timeZone);
  const out: Interval[] = [];
  while (cursor < range.end && out.length < MAX_BUCKETS) {
    if (size === 'month') month += 1;
    else day += size === 'week' ? 7 : 1;
    const next = zonedMidnight(year, month, day, timeZone);
    const start = Math.max(cursor, range.start);
    const end = Math.min(next, range.end);
    if (end > start) out.push({ start, end });
    cursor = next;
  }
  return out;
}

/** A sensible bucket for a range: days up to ~5 weeks, weeks up to ~6 months, months beyond. */
export function bucketSizeFor(range: Interval): BucketSize {
  const days = Math.floor(Math.max(0, range.end - range.start) / DAY_MS);
  return days <= 35 ? 'day' : days <= 190 ? 'week' : 'month';
}

/** ISO-8601 week number ("KW 39") of the local date at instant `t`: weeks start on Monday, week 1 holds the year's first Thursday. */
export function isoWeekNumber(t: number, timeZone: string): number {
  const wall = wallClock(t, timeZone);
  // The week's Thursday decides which year the week belongs to.
  const thursday = Date.UTC(wall.getUTCFullYear(), wall.getUTCMonth(), wall.getUTCDate() + 3 - isoWeekday(wall));
  const newYear = Date.UTC(new Date(thursday).getUTCFullYear(), 0, 1);
  return Math.floor((thursday - newYear) / (7 * DAY_MS)) + 1;
}

/** One agent session's reported cost (spec §9c). */
export interface TokenCostSession {
  project: string | null;
  startedAt: number;
  costMicroUSD: number;
}

/** How token costs enter invoices: the price of one US dollar × 10 000, and a markup in basis points. */
export interface TokenCostSettings {
  usdRate: number;
  markup: number;
}

export interface TokenCostLine {
  project: string;
  costMicroUSD: number;
  usdCents: number;
  amountCents: number;
}

/**
 * Token costs per billable project (spec §9c): the reported costs of the sessions that started in `range`, converted
 * with the dollar rate (10 000 for projects billed in USD) and the markup. Projects whose costs round to 0 get no line.
 */
export function tokenCosts(
  sessions: readonly TokenCostSession[],
  projects: readonly BillingProject[],
  settings: TokenCostSettings,
  currencies: Map<string, string> | Readonly<Record<string, string>> = {},
  range?: Interval,
): Map<string, TokenCostLine> {
  const billable = new Set(projects.filter((p) => p.billable).map((p) => p.id));
  const currencyOf = currencies instanceof Map ? currencies : new Map(Object.entries(currencies));
  const sums = new Map<string, number>();
  for (const s of sessions) {
    if (s.project === null || !billable.has(s.project) || s.costMicroUSD <= 0) continue;
    if (range && (s.startedAt < range.start || s.startedAt >= range.end)) continue;
    sums.set(s.project, (sums.get(s.project) ?? 0) + s.costMicroUSD);
  }
  const out = new Map<string, TokenCostLine>();
  for (const [project, costMicroUSD] of sums) {
    const usdCents = Math.floor((costMicroUSD + 5000) / 10_000);
    if (usdCents <= 0) continue;
    const rate = currencyOf.get(project) === 'USD' ? 10_000 : settings.usdRate;
    // Exact integers: the product can exceed 2^53 for large amounts.
    const amountCents = Number((BigInt(usdCents) * BigInt(rate) * BigInt(10_000 + settings.markup) + 50_000_000n) / 100_000_000n);
    out.set(project, { project, costMicroUSD, usdCents, amountCents });
  }
  return out;
}

export interface InvoiceLine {
  project: string;
  /** Merged billable time, exact. */
  billedMs: number;
  /** After export rounding (per merged interval, always up). */
  roundedMs: number;
  rateCents: number | null;
  amountCents: number;
}

export interface InvoiceSummary {
  currency: string;
  lines: InvoiceLine[];
  /** Token costs (§9c), one per project, in the same order; their amounts are part of `netCents`. */
  tokenLines: TokenCostLine[];
  netCents: number;
  /** VAT in basis points (1900 = 19 %). */
  vatBasisPoints: number;
  vatCents: number;
  grossCents: number;
}

/**
 * Invoice-ready summaries based on merged billable time, rounded as each project's export setting says. One per currency
 * (sorted; projects without one bill in EUR). Lines follow `order` (project ids); projects without billable time are left out.
 * Token cost lines (§9c) join their project's currency.
 */
export function invoiceSummaries(
  totals: ReadonlyMap<string, ProjectTotals>,
  projects: readonly BillingProject[],
  currencies: Map<string, string> | Readonly<Record<string, string>>,
  order: readonly string[],
  vatBasisPoints: number,
  tokens: ReadonlyMap<string, TokenCostLine> = new Map(),
): InvoiceSummary[] {
  const byId = new Map(projects.map((p) => [p.id, p]));
  const currencyOf = currencies instanceof Map ? currencies : new Map(Object.entries(currencies));
  const lines = new Map<string, InvoiceLine[]>();
  const tokenLines = new Map<string, TokenCostLine[]>();
  for (const id of order) {
    const currency = currencyOf.get(id) ?? 'EUR';
    const token = tokens.get(id);
    if (token) tokenLines.set(currency, [...(tokenLines.get(currency) ?? []), token]);
    const t = totals.get(id);
    const project = byId.get(id);
    if (!t || !project || t.billableMs <= 0) continue;
    const rounded = roundForExport(t.billableIntervals, project.roundingMinutes, project.rateCents);
    const list = lines.get(currency) ?? [];
    list.push({ project: id, billedMs: t.billableMs, roundedMs: rounded.roundedMs, rateCents: project.rateCents, amountCents: rounded.amountCents });
    lines.set(currency, list);
  }
  return [...new Set([...lines.keys(), ...tokenLines.keys()])].sort().map((currency) => {
    const list = lines.get(currency) ?? [];
    const tokenList = tokenLines.get(currency) ?? [];
    const netCents = list.reduce((sum, line) => sum + line.amountCents, 0) + tokenList.reduce((sum, line) => sum + line.amountCents, 0);
    const vat = vatCents(netCents, vatBasisPoints);
    return { currency, lines: list, tokenLines: tokenList, netCents, vatBasisPoints, vatCents: vat, grossCents: netCents + vat };
  });
}

/** `round(net × basisPoints / 10 000)`, half up. `0` without VAT or a positive net amount. */
export function vatCents(netCents: number, basisPoints: number): number {
  if (basisPoints <= 0 || netCents <= 0) return 0;
  return Math.floor((netCents * basisPoints + 5000) / 10_000);
}
